import('../../styles/tcms_travel_cta.scss');

/**
 * @namespace TcmsTravelCta
 * @version 1.0.0
 */
class TcmsTravelCta {

    constructor(options) {
        options = options || {};

        this._options = {
            prependToNode: options.prependToNode || null,
            appendToNode: options.appendToNode || null,
            insertAfterNode: options.insertAfterNode || null,
            classes: options.classes || ['travel_cta', 'tcms_floating_sidebar'],
            travelCtaData: options.travelCtaData || {},
            useFixedPosition: !!options.useFixedPosition,
            eventTackingFunc: options.eventTackingFunc || null,
            offset: {
                top: options?.offset?.top ?? 10,
                right: options?.offset?.right ?? 20
            }
        };

        this.initialized = false;
        this.initialize();
    }

    /**
     * initialize the travel cta
     * @return {boolean}
     */
    initialize() {
        if (this.initialized) {
            return true;
        }

        this.travelCtaNode = this.create(this._options);

        if (!this.travelCtaNode) {
            return false;
        }

        this.initialized = true;

        if (this._options.prependToNode) {
            this.prependTo(this._options.prependToNode, this.travelCtaNode);
        } else if (this._options.insertAfterNode) {
            this.insertAfter(this._options.insertAfterNode, this.travelCtaNode);
        } else if (this._options.appendToNode) {
            this.appendTo(this._options.appendToNode, this.travelCtaNode);
        }

        if (this._options.eventTackingFunc) {
            this._options.eventTackingFunc.addListener(this.travelCtaNode.querySelector('a'), null, null);
        }

        if (this._options.useFixedPosition) {
            const options = {
                travelCtaNode: this.travelCtaNode,
                offset: this._options.offset
            };

            this.setOffsetProperties(this.travelCtaNode, options);

            window.addEventListener('scroll', () => {
                const observer = this.getObserver(this.handlePositionFixation, options);

                observer.observe(this._options.prependToNode);
            });
        }
    }

    /**
     * create TravelCta node
     *
     * @return {Node|null}
     */
    create(options) {
        this.options = {
            travelCtaData: options.travelCtaData || {},
            classes: options.classes || []
        };

        if (!this.options.travelCtaData) {
            console.error(
                'TcmsTravelCta: ctaCollection like [{title: \'Foo\', url: \'/fooo\', attributes: {attr: \'fooo\'}}] is missing'
            );
            return null;
        }

        let travelCtaNode = document.createElement('div');

        travelCtaNode.classList.add(...this.options.classes);

        this.options.travelCtaData.forEach((ctaData) => {
            if (ctaData && ctaData.hasOwnProperty('title')) {
                let ctaTextNode = document.createElement('span');

                ctaTextNode.innerText = ctaData.title;

                let ctaNode = document.createElement('a');

                ctaNode.appendChild(ctaTextNode);

                if (ctaData.hasOwnProperty('attributes')) {
                    for (let [attribute, value] of Object.entries(ctaData.attributes)) {
                        if (typeof value === 'object') {
                            value = JSON.stringify(value);
                        }
                        ctaNode.setAttribute(attribute, value);
                    }
                }

                travelCtaNode.appendChild(ctaNode);
            }
        });

        return travelCtaNode;
    }

    /**
     * Set node width as negative margin-left on desktop
     *
     * @param {Node} node
     * @return {IntersectionObserver}
     */
    setOffsetProperties(node, options) {
        window.fireOnVisible(node || null, async(elem, entry, io) => {
            if (entry.intersectionRect?.width >= 1025) {
                elem.setAttribute(
                    'style',
                    'margin-left:-' + Math.abs(-(entry.boundingClientRect.width + options.offset.right)) + 'px;'
                );
            }
        });
    }

    /**
     * Handle should add or remove position fixation class at travelCtaNode
     *
     * @param {IntersectionObserverEntry} entry
     * @param {Object} options
     *
     * @return {void}
     */
    handlePositionFixation(entry, options) {
        const bounds = entry.boundingClientRect;

        if (bounds.top <= options.offset.top) {
            options.travelCtaNode.classList.add('fixed');
        } else if (bounds.top >= options.offset.top) {
            options.travelCtaNode.classList.remove('fixed');
        }
    }

    /**
     * Return a IntersectionObserver object
     *
     * @param {Function} callBackFunction
     * @param {Object} options
     *
     * @return {IntersectionObserver}
     */
    getObserver(callBackFunction, options) {
        const observer = new IntersectionObserver((entries) => {
            for (const entry of entries) {
                callBackFunction(entry, options);
            }

            observer.disconnect();
        });

        return observer;
    }

    /**
     * Prepend TravelCta to node
     *
     * @param {Node} prependToNode
     * @param {Node} travelCtaNode
     */
    prependTo(prependToNode, travelCtaNode) {
        if (!prependToNode || !travelCtaNode) {
            return null;
        }

        prependToNode.prepend(travelCtaNode);
    }

    /**
     * Append TravelCta to node
     *
     * @param {Node} appendToNode
     * @param {Node} travelCtaNode
     */
    appendTo(appendToNode, travelCtaNode) {
        if (!appendToNode || !travelCtaNode) {
            return null;
        }

        appendToNode.appendChild(travelCtaNode);
    }

    /**
     * Insert TravelCta after a node
     *
     * @param {Node} referenceNode
     * @param {Node} travelCtaNode
     */
    insertAfter(referenceNode, travelCtaNode) {
        if (!referenceNode || !travelCtaNode) {
            return null;
        }

        referenceNode.parentNode.insertBefore(travelCtaNode, referenceNode.nextSibling);
    }

}

export { TcmsTravelCta };
